<script setup lang="ts">
import { Button } from '@forgd/ui-kit/button'
import { externalLinks } from '#core/utils/external-links'
import { isMobileReleased, mobilePathPrefixes } from '#core/utils/mobile-gate'
import { useWindowSize } from '@vueuse/core'

defineProps<{
  debug?: boolean
}>()

// constants
const route = useRoute()
const auth = useAuth()
const { mobileGateDisabled } = useRuntimeConfig().public
const portal = useRuntimeConfig().public.portal ?? null
const isLiquidityPortal = portal === 'liquidity'

// widths
const { width: pageWidth } = useWindowSize()
const screenWidth = ref(screen?.width || 0)
const MIN_WIDTH = 800

// variables
const isMobile = computed(() => {
  return screenWidth.value <= MIN_WIDTH
})

const mobileReleased = computed(() => isMobileReleased(route.path, mobilePathPrefixes(auth.featureFlags, portal)))

const isVisible = computed(() => {
  const isNarrow = pageWidth.value <= MIN_WIDTH || screenWidth.value < MIN_WIDTH

  if (mobileReleased.value) {
    return false
  }

  if (isLiquidityPortal) {
    return isNarrow && !mobileGateDisabled
  }

  if (route.meta.supportsMobile || mobileGateDisabled) {
    return false
  }
  return isNarrow
})

// set page overflow to hidden when the gate is visible
function setOverflow(value: string) {
  if (document) {
    document.querySelector('html')!.style.overflow = value
    document.querySelector('body')!.style.overflow = value
  }
}

watch(isVisible, (value: boolean) => {
  setOverflow(value ? 'hidden' : '')
}, { immediate: true })

// needed for chrome devtools responsive mode
function onResize() {
  screenWidth.value = screen?.width || 0
}
onMounted(() => {
  window.addEventListener('resize', () => onResize)
})
onUnmounted(() => {
  window.removeEventListener('resize', () => onResize)
  setOverflow('')
})
</script>

<template>
  <div
    v-if="isVisible"
    class="
      fixed inset-0
      flex flex-col items-center justify-center
      bg-neutral-200
    "
    style="z-index:9999999999"
  >
    <div class="max-w-[320px] mx-auto flex flex-col gap-10 items-center justify-center grow">
      <img src="/images/desktop.svg">

      <div class="space-y-2 text-center">
        <h1 class="text-2xl">
          Forgd app is a desktop application
        </h1>
        <div class="text-primal">
          <p v-if="isMobile">
            Open our website in a desktop browser to register or access your account
          </p>
          <p v-else>
            Resize your browser window to continue
          </p>
        </div>
      </div>

      <Button v-if="isMobile" as-child>
        <NuxtLink :to="externalLinks.home">
          Go to forgd.com
        </NuxtLink>
      </Button>
    </div>
  </div>
  <pre
    v-if="debug"
    class="fixed bottom-4 right-4 p-4 text-xs bg-white rounded-md outline-dashed outline-red-500"
    style="z-index:9999999999"
  >{{
      {
        screenWidth,
        pageWidth,
        isVisible,
        isMobile,
        mobileReleased,
        mobileGateDisabled,
        meta: route.meta,
      }
    }}
  </pre>
</template>
